import { useMutation } from '@apollo/client'
import { PhoneNumberHelpers } from '@dai/common'
import { TitledPaper, TypedFormik } from '@dai/components'
import { PersonOutline } from '@mui/icons-material'
import { Alert, Button, Stack, TextField } from '@mui/material'
import { logAnalyticsEvent } from 'api/Firebase'
import { useEffect, useState } from 'react'
import * as Yup from 'yup'

import {
  CREATE_USER_PROFILE_IMAGE,
  CreateUserProfileImageInput,
  CreateUserProfileImageMutation,
  CreateUserProfileImageMutationVariables,
  UPDATE_USER,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import PhotoUpload from '@/pages/forms/components/PhotoUpload'
import { uploadImageAndGetData } from '@/utils/image.helpers'

import { AccountSetUpStepProps } from '../types'

type ProfileSetUpProps = AccountSetUpStepProps

function ProfileSetUp(props: ProfileSetUpProps) {
  const { t } = useT()
  return (
    <TitledPaper
      title={t('profileSetUp.title')}
      subtitle={t('profileSetUp.subtitle')}
    >
      <ProfileSetUpBody {...props} />
    </TitledPaper>
  )
}

function ProfileSetUpBody({ nextStep, user }: ProfileSetUpProps) {
  const { t } = useT()
  const [image, setImage] = useState<any>(null)
  const [imagePreview, setImagePreview] = useState<string | null | undefined>(
    null,
  )

  useEffect(() => {
    if (user?.profileImage?.url) {
      setImagePreview(user?.profileImage?.url)
    }
  }, [user?.profileImage?.url])

  const [createUserProfileImage] = useMutation<
    CreateUserProfileImageMutation,
    CreateUserProfileImageMutationVariables
  >(CREATE_USER_PROFILE_IMAGE)

  const [updateUser] = useMutation<
    UpdateUserMutation,
    UpdateUserMutationVariables
  >(UPDATE_USER)

  return (
    <TypedFormik
      initialValues={{
        phoneNumber: user?.phoneNumber
          ? PhoneNumberHelpers.formatPhoneNumber(user.phoneNumber)
          : '',
        jobTitle: user?.jobTitle || '',
      }}
      validationSchema={Yup.object().shape({
        phoneNumber: Yup.string()
          .required(t('profileSetUp.validation.phoneNumberRequired'))
          .test(
            'is-valid-phone',
            t('profileSetUp.validation.invalidPhoneNumber'),
            value => PhoneNumberHelpers.PHONE_NUMBER_REGEX.test(value),
          ),
        jobTitle: Yup.string()
          .max(255)
          .required(t('profileSetUp.validation.jobTitleRequired')),
      })}
      onSubmit={async (values, { setStatus }) => {
        try {
          if (!user) throw new Error(t('profileSetUp.error.userNotFound'))

          if (image) {
            const { bucketFileName, url } = await uploadImageAndGetData({
              image,
              folder: 'user-profile-images',
            })

            const imageData: CreateUserProfileImageInput = {
              bucketFileName,
              url,
              user: { set: user.id },
            }

            await createUserProfileImage({
              variables: {
                data: imageData,
              },
            })
          }

          const rawPhoneNumber = PhoneNumberHelpers.unformatPhoneNumber(
            values.phoneNumber,
          )

          await updateUser({
            variables: {
              data: {
                id: user.id,
                phoneNumber: rawPhoneNumber,
                jobTitle: values.jobTitle,
              },
            },
          })
          setStatus({ success: true })
          logAnalyticsEvent('set_up_profile_complete')
          nextStep()
        } catch (error: any) {
          const message = error.message || t('common.somethingWentWrong')
          setStatus({ success: false, message })
        }
      }}
    >
      {({
        status,
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Stack spacing={4}>
            {status?.message && (
              <Alert severity={status.success ? 'success' : 'warning'}>
                {status.message}
              </Alert>
            )}
            <PhotoUpload
              container="circle"
              setImage={setImage}
              imagePreview={imagePreview}
              setImagePreview={setImagePreview}
              Button={
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PersonOutline />}
                  fullWidth
                  component="span" // important for some reason
                >
                  {imagePreview
                    ? t('profileSetUp.changePhoto')
                    : t('profileSetUp.uploadPhoto')}
                </Button>
              }
            />
            <TextField
              type="tel"
              name="phoneNumber"
              label={t('profileSetUp.phoneNumber')}
              value={values.phoneNumber}
              error={Boolean(touched.phoneNumber && errors.phoneNumber)}
              fullWidth
              helperText={touched.phoneNumber && errors.phoneNumber}
              onBlur={handleBlur}
              onChange={e => {
                const formattedPhoneNumber =
                  PhoneNumberHelpers.formatPhoneNumber(e.target.value)
                setFieldValue('phoneNumber', formattedPhoneNumber)
              }}
              placeholder="(XXX) XXX-XXXX"
            />
            <TextField
              type="text"
              name="jobTitle"
              label={t('profileSetUp.jobTitle')}
              value={values.jobTitle}
              error={Boolean(touched.jobTitle && errors.jobTitle)}
              fullWidth
              helperText={touched.jobTitle && errors.jobTitle}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              loading={isSubmitting}
              loadingPosition="end"
            >
              {t('common.continue')}
            </Button>
          </Stack>
        </form>
      )}
    </TypedFormik>
  )
}

export default ProfileSetUp
